<!--
  vue-slick-carousel の置き換え。
  vue-slick-carousel は Vue 2 の VNode を直接複製する実装で、Vue 3 では
  「children.map is not a function」で落ちる。後継も無いので、このアプリが使っている機能
  （N 枚表示・N 枚送り・矢印・ドット・ループ無し・スワイプ無し）だけを実装している。

  既存の .slick-* のスタイル（SlickCarouselMixin.vue のグローバルスタイル）をそのまま効かせるため、
  DOM の構造とクラス名は vue-slick-carousel（react-slick）と同じにしてある。
  既定値も vue-slick-carousel に合わせている。
-->
<script>
import { h, cloneVNode, Comment, Fragment } from 'vue'
import './slick.css'
import './slick-theme.css'

// v-for は Fragment として 1 つの VNode にまとまって渡ってくるので、スライド単位にばらす
const flatten = (vnodes) => vnodes.flatMap((vnode) => {
  if (vnode.type === Comment) return []
  if (vnode.type === Fragment) return flatten(vnode.children || [])
  return [vnode]
})

export default {
  name: 'SlickCarousel',

  // 移行ビルド上でも Vue 3 の挙動で動かす（描画関数を Vue 3 の h で書いているため）
  compatConfig: { MODE: 3 },

  props: {
    arrows: { type: Boolean, default: true },
    dots: { type: Boolean, default: false },
    slidesToShow: { type: Number, default: 1 },
    slidesToScroll: { type: Number, default: 1 },
    speed: { type: Number, default: 500 },
    // 以下の 2 つは false の挙動しか実装していない（このアプリでは false でしか使っていない）
    infinite: { type: Boolean, default: false },
    swipe: { type: Boolean, default: false },
  },

  data () {
    return {
      current: 0,
      listWidth: 0,
    }
  },

  mounted () {
    this.measure()
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.measure)
      this.resizeObserver.observe(this.$refs.list)
    } else {
      window.addEventListener('resize', this.measure)
    }
  },

  beforeUnmount () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.measure)
    }
  },

  methods: {
    measure () {
      if (this.$refs.list) this.listWidth = this.$refs.list.offsetWidth
    },

    // ループしないので、最後のページは「末尾の N 枚が見えている位置」で止める（react-slick と同じ）
    lastIndex (count) {
      return Math.max(count - this.slidesToShow, 0)
    },

    goTo (index, count) {
      this.current = Math.min(Math.max(index, 0), this.lastIndex(count))
    },
  },

  render () {
    const slides = flatten(this.$slots.default ? this.$slots.default() : [])
    const count = slides.length
    // スライドの数が表示枚数以下なら、矢印もドットも出さない（react-slick の unslick と同じ）
    const enabled = count > this.slidesToShow
    // スライドが減ったときに範囲外へ出ないよう、描画では切り詰めた値を使う
    const current = Math.min(this.current, this.lastIndex(count))

    const slideWidth = this.listWidth / this.slidesToShow
    const pageStarts = []
    for (let i = 0; i * this.slidesToScroll < count; i++) {
      const start = Math.min(i * this.slidesToScroll, this.lastIndex(count))
      if (pageStarts[pageStarts.length - 1] !== start) pageStarts.push(start)
    }

    const arrow = (dir) => {
      const disabled = dir === 'prev' ? current <= 0 : current >= this.lastIndex(count)
      return h('button', {
        type: 'button',
        class: ['slick-arrow', `slick-${dir}`, { 'slick-disabled': disabled }],
        onClick: () => this.goTo(current + (dir === 'prev' ? -1 : 1) * this.slidesToScroll, count),
      }, dir === 'prev' ? 'Previous' : 'Next')
    }

    const track = h('div', {
      class: 'slick-track',
      style: {
        width: `${slideWidth * count}px`,
        transform: `translate3d(${-current * slideWidth}px, 0px, 0px)`,
        transition: `transform ${this.speed}ms ease`,
      },
    }, slides.map((vnode, i) => {
      const active = i >= current && i < current + this.slidesToShow
      return h('div', {
        key: vnode.key != null ? vnode.key : i,
        class: ['slick-slide', { 'slick-active': active, 'slick-current': i === current }],
        style: { width: `${slideWidth}px` },
        'aria-hidden': active ? 'false' : 'true',
      // react-slick と同じく子要素に幅と inline-block を付ける（<li> の行頭記号もこれで消える）
      }, [h('div', null, [cloneVNode(vnode, { style: { width: '100%', display: 'inline-block' }, tabindex: '-1' })])])
    }))

    return h('div', { class: 'slick-slider slick-initialized', dir: 'ltr' }, [
      enabled && this.arrows ? arrow('prev') : null,
      // 呼び出し側で .other-item-list（display: flex）が付くため、幅を明示しないと
      // リストが中身の幅（= 0）に縮んで、スライド幅の計算が 0 のまま循環する
      h('div', { class: 'slick-list', ref: 'list', style: { width: '100%' } }, [track]),
      enabled && this.arrows ? arrow('next') : null,
      enabled && this.dots
        ? h('ul', { class: 'slick-dots', style: { display: 'block' } }, pageStarts.map((start, i) =>
          h('li', { key: start, class: { 'slick-active': current === start } }, [
            h('button', { type: 'button', onClick: () => this.goTo(start, count) }, String(i + 1)),
          ])))
        : null,
    ])
  },
}
</script>
